import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { updateWarehouseSchema } from '@eombor/contracts';
import { Button, Dialog, Input, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';
import { warehouseApi, type WarehouseDto } from '../api/warehouse.api.js';
import { warehouseKeys } from '../model/warehouse.queries.js';

/**
 * OMBOR MA'LUMOTLARINI TAHRIRLASH.
 *
 * KOD va KORXONA bu yerda YO'Q va ataylab:
 *
 *   - kod etiketkalarga bosilgan va hujjatlarda uchraydi. Uni o'zgartirish
 *     omborda allaqachon yopishtirilgan har bir shtrix-kodni yaroqsiz
 *     qilardi (`updateWarehouseSchema` ham uni qabul qilmaydi);
 *   - korxonani almashtirish omborni boshqa MTU ga ko'chirish degani, ya'ni
 *     kim ko'rishini o'zgartiradi. Bu forma tahriri emas, hujjat bilan
 *     rasmiylashtiriladigan amal.
 *
 * O'LCHAMNI KICHRAYTIRISH backendda tekshiriladi: zonasi tashqarida qolib
 * ketadigan o'lcham rad etiladi va xabar qaysi zona to'sqinlik qilganini
 * aytadi. Bu tekshiruv frontendda TAKRORLANMAYDI — zonalar ro'yxati bu
 * oynada yo'q va uni faqat shu forma uchun yuklash server tekshiruvini
 * ikkilantirgan bo'lardi.
 */

export interface EditWarehouseDialogProps {
  open: boolean;
  onClose: () => void;
  warehouse: WarehouseDto;
}

/** Vergul bilan terilgan o'lcham ham qabul qilinadi — klaviatura odati. */
const toNumber = (value: string): number => {
  const normalized = value.trim().replace(',', '.');
  return normalized === '' ? Number.NaN : Number(normalized);
};

const errorsByField = (issues: readonly { path: PropertyKey[]; message: string }[]) => {
  const map = new Map<string, string>();
  for (const issue of issues) {
    const key = issue.path.join('.');
    if (!map.has(key)) map.set(key, issue.message);
  }
  return map;
};

export const EditWarehouseDialog = ({ open, onClose, warehouse }: EditWarehouseDialogProps) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(warehouse.name);
  const [address, setAddress] = useState(warehouse.address ?? '');
  const [lengthM, setLengthM] = useState(String(warehouse.dims.lengthM));
  const [widthM, setWidthM] = useState(String(warehouse.dims.widthM));
  const [heightM, setHeightM] = useState(String(warehouse.dims.heightM));
  const [isActive, setIsActive] = useState(warehouse.isActive);
  const [submitted, setSubmitted] = useState(false);

  /*
   * Har ochilishda qiymatlar SERVERDAGI holatga qaytariladi. Oyna yopilib
   * qayta ochilganda yarim tahrirlangan forma qolib ketsa, operator o'zi
   * bekor qilgan o'zgarishni saqlab yuborishi mumkin edi.
   */
  useEffect(() => {
    if (!open) return;
    setName(warehouse.name);
    setAddress(warehouse.address ?? '');
    setLengthM(String(warehouse.dims.lengthM));
    setWidthM(String(warehouse.dims.widthM));
    setHeightM(String(warehouse.dims.heightM));
    setIsActive(warehouse.isActive);
    setSubmitted(false);
  }, [open, warehouse]);

  const draft = useMemo(
    () => ({
      name: name.trim(),
      // Bo'sh manzil `null` — bo'sh satr emas. Bazada ikki xil "manzil yo'q"
      // paydo bo'lsa, ro'yxatda filtrlash ham, saralash ham ishonchsiz.
      address: address.trim() === '' ? null : address.trim(),
      dims: { lengthM: toNumber(lengthM), widthM: toNumber(widthM), heightM: toNumber(heightM) },
      isActive,
    }),
    [name, address, lengthM, widthM, heightM, isActive],
  );

  const parsed = updateWarehouseSchema.safeParse(draft);
  const fieldErrors = submitted && !parsed.success ? errorsByField(parsed.error.issues) : new Map();
  const errorOf = (field: string): string | undefined => fieldErrors.get(field);

  const save = useMutation({
    mutationFn: () => {
      if (!parsed.success) throw new Error('invalid');
      return warehouseApi.update(warehouse.id, parsed.data);
    },
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
      toast.show(t('warehouse.editSaved', { name: updated.name }), 'success');
      onClose();
    },
  });

  const area = parsed.success ? Math.round(draft.dims.lengthM * draft.dims.widthM) : null;

  /* Server xatosi (masalan "A zonasi sig'maydi") formaning ustida turadi:
     u maydonga tegishli emas — butun o'lchamlar to'plamiga tegishli. */
  const serverError =
    save.error instanceof ApiClientError ? save.error.message : save.isError ? t('warehouse.editError') : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('warehouse.editTitle')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('warehouse.newCancel')}
          </Button>
          <Button
            loading={save.isPending}
            onClick={() => {
              setSubmitted(true);
              if (parsed.success) save.mutate();
            }}
          >
            <Check size={16} aria-hidden />
            {t('warehouse.editSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-text-muted text-xs tracking-wide uppercase">
            {t('warehouse.editImmutable')}
          </span>
          <span className="tabular text-sm text-text">
            {warehouse.code}
          </span>
        </div>

        {serverError === null ? null : (
          <p
            role="alert"
            className="border-danger/30 bg-danger-soft text-danger rounded-[var(--radius-sm)] border px-3 py-2 text-sm text-pretty"
          >
            {serverError}
          </p>
        )}

        <Input
          label={t('warehouse.newName')}
          value={name}
          error={errorOf('name')}
          onChange={(event) => setName(event.target.value)}
        />

        <Input
          label={t('warehouse.newAddress')}
          value={address}
          error={errorOf('address')}
          onChange={(event) => setAddress(event.target.value)}
        />

        <div className="grid grid-cols-3 gap-3">
          <Input
            mono
            inputMode="decimal"
            label={t('warehouse.newLength')}
            value={lengthM}
            error={errorOf('dims.lengthM')}
            onChange={(event) => setLengthM(event.target.value)}
          />
          <Input
            mono
            inputMode="decimal"
            label={t('warehouse.newWidth')}
            value={widthM}
            error={errorOf('dims.widthM')}
            onChange={(event) => setWidthM(event.target.value)}
          />
          <Input
            mono
            inputMode="decimal"
            label={t('warehouse.newHeight')}
            value={heightM}
            error={errorOf('dims.heightM')}
            onChange={(event) => setHeightM(event.target.value)}
          />
        </div>

        {area === null ? null : (
          <p className="text-text-muted text-sm">{t('warehouse.newArea', { area })}</p>
        )}

        {/* Faollik — o'chirish (`DELETE`) o'rniga. Ombor yopilganda undagi
            qoldiq va hujjatlar tarixda qolishi kerak, shuning uchun yozuv
            hech qachon o'chirilmaydi, faqat nofaol bo'ladi. */}
        <Select
          label={t('warehouse.editStatus')}
          value={isActive ? 'active' : 'inactive'}
          options={[
            { value: 'active', label: t('warehouse.statusActive') },
            { value: 'inactive', label: t('warehouse.statusInactive') },
          ]}
          onChange={(event) => setIsActive(event.target.value === 'active')}
        />
      </div>
    </Dialog>
  );
};
